import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const RawMaterialPlan = () => {
  const [filters, setFilters] = useState({
    plan_month: new Date().toISOString().slice(0, 7), // YYYY-MM format
    steel_code: '',
    mill_code: '',
    status: 'all'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: planData, isLoading, error, refetch } = useQuery(
    ['rawMaterialPlan', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/raw-material/plan?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching raw material plan: ${error.message}`, { variant: 'error' });
      },
      enabled: false
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned': return 'info';
      case 'ordered': return 'warning';
      case 'received': return 'success';
      case 'delayed': return 'error';
      default: return 'default';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading raw material plan: {error.message}
        </Alert>
      </Box>
    );
  }

  const mockData = [
    {
      steel_code: 'EN24',
      steel_name: 'Nickel Chrome Molybdenum Steel',
      mill_code: 'SAIL',
      mill_name: 'Steel Authority of India Ltd',
      planned_quantity: 50.0,
      ordered_quantity: 45.0,
      received_quantity: 30.0,
      pending_quantity: 20.0,
      required_date: '2024-12-15',
      order_date: '2024-11-15',
      expected_date: '2024-12-10',
      status: 'ordered',
      priority: 'high',
      heat_requirements: 8
    },
    {
      steel_code: 'EN8',
      steel_name: 'Medium Carbon Steel',
      mill_code: 'TATA',
      mill_name: 'Tata Steel Limited',
      planned_quantity: 75.0,
      ordered_quantity: 75.0,
      received_quantity: 75.0,
      pending_quantity: 0.0,
      required_date: '2024-12-01',
      order_date: '2024-10-20',
      expected_date: '2024-11-25',
      status: 'received',
      priority: 'medium',
      heat_requirements: 12
    },
    {
      steel_code: '20MnCr5',
      steel_name: 'Manganese Chrome Steel',
      mill_code: 'JSW',
      mill_name: 'JSW Steel Limited',
      planned_quantity: 40.0,
      ordered_quantity: 0.0,
      received_quantity: 0.0,
      pending_quantity: 40.0,
      required_date: '2024-12-20',
      order_date: null,
      expected_date: '2024-12-18',
      status: 'planned',
      priority: 'high',
      heat_requirements: 6
    },
    {
      steel_code: '4140',
      steel_name: 'Chrome Molybdenum Steel',
      mill_code: 'RINL',
      mill_name: 'Rashtriya Ispat Nigam Ltd',
      planned_quantity: 30.0,
      ordered_quantity: 30.0,
      received_quantity: 15.0,
      pending_quantity: 15.0,
      required_date: '2024-12-05',
      order_date: '2024-11-01',
      expected_date: '2024-12-08',
      status: 'delayed',
      priority: 'high',
      heat_requirements: 5
    }
  ];

  const totalPlanned = mockData.reduce((sum, item) => sum + item.planned_quantity, 0);
  const totalOrdered = mockData.reduce((sum, item) => sum + item.ordered_quantity, 0);
  const totalReceived = mockData.reduce((sum, item) => sum + item.received_quantity, 0);
  const totalPending = mockData.reduce((sum, item) => sum + item.pending_quantity, 0);

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <InventoryIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Raw Material Planning
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Planning Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="month"
              label="Plan Month"
              InputLabelProps={{ shrink: true }}
              value={filters.plan_month}
              onChange={(e) => setFilters({ ...filters, plan_month: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Mill Code"
              value={filters.mill_code}
              onChange={(e) => setFilters({ ...filters, mill_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="planned">Planned</MenuItem>
                <MenuItem value="ordered">Ordered</MenuItem>
                <MenuItem value="received">Received</MenuItem>
                <MenuItem value="delayed">Delayed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate Plan
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {totalPlanned.toFixed(1)} MT
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Planned
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {totalOrdered.toFixed(1)} MT
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Ordered
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {totalReceived.toFixed(1)} MT
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Received
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="error.main">
                    {totalPending.toFixed(1)} MT
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Pending
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Raw Material Plan Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Raw Material Procurement Plan
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Month: {new Date(filters.plan_month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Steel Grade</strong></TableCell>
                    <TableCell><strong>Mill/Supplier</strong></TableCell>
                    <TableCell align="right"><strong>Planned (MT)</strong></TableCell>
                    <TableCell align="right"><strong>Ordered (MT)</strong></TableCell>
                    <TableCell align="right"><strong>Received (MT)</strong></TableCell>
                    <TableCell align="center"><strong>Progress</strong></TableCell>
                    <TableCell><strong>Required Date</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Priority</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No raw material planning data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mockData.map((record, index) => {
                      const progress = (record.received_quantity / record.planned_quantity) * 100;
                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box>
                              <Chip 
                                label={record.steel_code} 
                                color="primary" 
                                variant="outlined" 
                                size="small"
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption" display="block" color="textSecondary">
                                {record.steel_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {record.mill_code}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {record.mill_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {record.planned_quantity.toFixed(1)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {record.ordered_quantity.toFixed(1)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              color={record.received_quantity > 0 ? "success.main" : "textSecondary"}
                            >
                              {record.received_quantity.toFixed(1)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Box sx={{ width: '80px', mr: 1 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={progress} 
                                  color={getProgressColor(progress)}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                              <Typography variant="caption" fontWeight="bold">
                                {progress.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {new Date(record.required_date).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={record.status.toUpperCase()} 
                              color={getStatusColor(record.status)}
                              size="small"
                              icon={
                                record.status === 'received' ? <CheckIcon /> :
                                record.status === 'delayed' ? <WarningIcon /> :
                                record.status === 'ordered' ? <TrendingUpIcon /> :
                                <ScheduleIcon />
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Avatar 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                bgcolor: record.priority === 'high' ? 'error.main' : 
                                        record.priority === 'medium' ? 'warning.main' : 'success.main',
                                fontSize: '12px'
                              }}
                            >
                              {record.priority === 'high' ? 'H' : 
                               record.priority === 'medium' ? 'M' : 'L'}
                            </Avatar>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Planning Notes */}
          <Paper sx={{ mt: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Planning Notes & Guidelines
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" paragraph>
                  <strong>Lead Times:</strong>
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>Standard Steel Grades: 15-20 days</li>
                  <li>Special Alloy Steels: 25-30 days</li>
                  <li>Import Materials: 45-60 days</li>
                </ul>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" paragraph>
                  <strong>Priority Levels:</strong>
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li><strong>High:</strong> Critical production requirements</li>
                  <li><strong>Medium:</strong> Normal production flow</li>
                  <li><strong>Low:</strong> Buffer stock maintenance</li>
                </ul>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RawMaterialPlan;